'use client';

import type { ReactNode } from 'react';

import type { Breakpoint } from '@commons/types/styles';

import { useMediaQueries } from './provider';

// Type definition for the MediaQuery component properties.

type Props = {
  children: ReactNode;
  min?: Breakpoint;
  max?: Breakpoint;
  fallback?: ReactNode;
};

/**
 * Server-safe MediaQuery component.
 *
 * Renders children only if the specified media query matches.
 * Also prevents hydration mismatch errors by returning `null` if the component has not yet mounted.
 *
 * This component uses the Donut pattern to ensure compatibility when rendered in a server component.
 *
 * IMPORTANT:
 *
 * This component should not be used if children needs to be in the DOM for SEO purposes,
 * as `null` will be rendered during SSR and until the component mounts on the client.
 */

const MediaQuery = ({ children, min, max, fallback }: Props) => {
  const { ready, minWidth, maxWidth } = useMediaQueries();

  if (!ready) {
    return fallback ?? null;
  }

  if (min && !minWidth[min]) {
    return null;
  }

  if (max && !maxWidth[max]) {
    return null;
  }

  return children;
};

export default MediaQuery;
